// Enterprise Custom Hooks for Warranty Management
import { useState, useMemo } from 'react';
import type { Warranty, WarrantyFilters, WarrantyStats, ShareModalState } from '../types/warranty';
import { 
  calculateWarrantyStats, 
  filterWarranties, 
  getUniqueCategories, 
  getUniqueProviders,
  getWarrantyStatus
} from '../utils/warrantyUtils';

/**
 * Custom hook for managing warranty filters and search
 */
export const useWarrantyFilters = (warranties: Warranty[]) => {
  const [filters, setFilters] = useState<WarrantyFilters>({
    searchTerm: '',
    statusFilter: 'all',
    categoryFilter: 'all',
    providerFilter: 'all'
  });

  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  // Update warranties with computed status
  const warrantiesWithStatus = useMemo(() => 
    warranties.map(warranty => ({
      ...warranty,
      status: getWarrantyStatus(warranty)
    })),
    [warranties]
  );

  const filteredWarranties = useMemo(() => 
    filterWarranties(warrantiesWithStatus, filters),
    [warrantiesWithStatus, filters]
  );

  const stats = useMemo(() => 
    calculateWarrantyStats(warrantiesWithStatus),
    [warrantiesWithStatus]
  );

  const categories = useMemo(() => 
    getUniqueCategories(warranties),
    [warranties]
  );

  const providers = useMemo(() => 
    getUniqueProviders(warranties),
    [warranties]
  );

  const updateFilters = (newFilters: Partial<WarrantyFilters>) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  };

  const clearFilters = () => {
    setFilters({
      searchTerm: '',
      statusFilter: 'all',
      categoryFilter: 'all',
      providerFilter: 'all'
    });
  };

  const toggleAdvancedFilters = () => {
    setShowAdvancedFilters(prev => !prev);
  };

  return {
    filters,
    filteredWarranties,
    stats,
    categories,
    providers,
    showAdvancedFilters,
    updateFilters,
    clearFilters,
    toggleAdvancedFilters
  };
};

/**
 * Custom hook for managing expanded tags state
 */
export const useExpandedTags = () => {
  const [expandedTags, setExpandedTags] = useState<Set<string>>(new Set());

  const toggleTagExpansion = (warrantyId: string) => {
    setExpandedTags(prev => {
      const newSet = new Set(prev);
      if (newSet.has(warrantyId)) {
        newSet.delete(warrantyId);
      } else {
        newSet.add(warrantyId);
      }
      return newSet;
    });
  };

  return {
    expandedTags,
    toggleTagExpansion
  };
};

/**
 * Custom hook for managing share modal state
 */
export const useShareModal = () => {
  const [shareModal, setShareModal] = useState<ShareModalState>({
    isOpen: false,
    warranty: null
  });

  const openShareModal = (warranty: Warranty) => {
    setShareModal({ isOpen: true, warranty });
  };

  const closeShareModal = () => {
    setShareModal({ isOpen: false, warranty: null });
  };

  return {
    shareModal,
    openShareModal,
    closeShareModal
  };
};
